import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AppError,
  Button,
  Card,
  EmptyState,
  Loader,
  Pagination,
  Search,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  toast,
  type TagTone,
} from "@bfc-software/pallet";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as dateFns from "date-fns";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  useFtpApi,
  type Job,
  type JobLog,
  type JobStatus,
  type JobStatsWindow,
  type JobsByFileType,
} from "@bfc-software/bfc-web-ui/lib/ftp-api-client";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";
import { LocalStorage, logger } from "@bfc-software/app-toolbox";

import styles from "./ftp.module.css";

type StatusView = "all" | "active" | "attention" | "completed";
type RangeView = "all" | "24h" | "7d" | "30d";

type FtpSearch = {
  page: number;
  q: string;
  view: StatusView;
  range: RangeView;
  fileTypes: string;
  selected?: string;
};

const HISTORY_PAGE_SIZE = 25;
const ACTIVE_STATUSES: readonly JobStatus[] = ["PENDING", "PROCESSING"];
const ATTENTION_STATUSES: readonly JobStatus[] = [
  "FAILED",
  "PARTIAL",
  "TIMEOUT",
];
const COMPLETED_STATUSES: readonly JobStatus[] = ["COMPLETED"];
const STUCK_THRESHOLD_MS = 15 * 60 * 1000;
const ACTIVE_STATUS_PARAM = ACTIVE_STATUSES.join(",");
const ATTENTION_STATUS_PARAM = ATTENTION_STATUSES.join(",");
const COMPLETED_STATUS_PARAM = COMPLETED_STATUSES.join(",");

const STATUS_TONES: Record<JobStatus, TagTone> = {
  PENDING: "neutral",
  PROCESSING: "info",
  COMPLETED: "success",
  PARTIAL: "warning",
  FAILED: "danger",
  TIMEOUT: "danger",
};

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/ftp",
)({
  staticData: {
    crumb: (_: unknown, t: TFunction) => ({ label: t("page.ftp.crumb") }),
  },
  component: RouteComponent,
  validateSearch: (search?: Record<string, unknown>): FtpSearch => {
    const rawPage = parseInt(String(search?.["page"] ?? "0"), 10);
    const page = Number.isSafeInteger(rawPage) && rawPage >= 0 ? rawPage : 0;

    const allowedViews: StatusView[] = [
      "all",
      "active",
      "attention",
      "completed",
    ];
    const rawView = search?.["view"];
    const view =
      typeof rawView === "string" &&
      allowedViews.includes(rawView as StatusView)
        ? (rawView as StatusView)
        : "all";

    const allowedRanges: RangeView[] = ["all", "24h", "7d", "30d"];
    const rawRange = search?.["range"];
    const range =
      typeof rawRange === "string" &&
      allowedRanges.includes(rawRange as RangeView)
        ? (rawRange as RangeView)
        : "24h";

    const q = typeof search?.["q"] === "string" ? search["q"] : "";
    const rawFileTypes =
      typeof search?.["fileTypes"] === "string"
        ? search["fileTypes"]
        : typeof search?.["fileType"] === "string"
          ? search["fileType"]
          : "";
    const fileTypes = normalizeCsvValue(String(rawFileTypes ?? ""));
    const selected =
      typeof search?.["selected"] === "string" && search["selected"].length > 0
        ? (search["selected"] as string)
        : undefined;

    return {
      page,
      q,
      view,
      range,
      fileTypes,
      selected,
    };
  },
});

function parseCsvValue(value: string | undefined) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right));
}

function normalizeCsvValue(value: string | undefined) {
  return parseCsvValue(value).join(",");
}

function getStatusesParam(view: StatusView): string | undefined {
  switch (view) {
    case "active":
      return ACTIVE_STATUS_PARAM;
    case "attention":
      return ATTENTION_STATUS_PARAM;
    case "completed":
      return COMPLETED_STATUS_PARAM;
    default:
      return undefined;
  }
}

function getRangeFrom(range: RangeView, now: number): string | undefined {
  if (range === "all") return undefined;
  const offsetMs =
    range === "24h"
      ? 24 * 60 * 60 * 1000
      : range === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000;
  return new Date(now - offsetMs).toISOString();
}

function isActiveStatus(status: JobStatus) {
  return ACTIVE_STATUSES.includes(status);
}

function isAttentionStatus(status: JobStatus) {
  return ATTENTION_STATUSES.includes(status);
}

function useTickingNow(intervalMs: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatDuration(milliseconds: number | null | undefined, t: TFunction) {
  if (typeof milliseconds !== "number" || Number.isNaN(milliseconds)) {
    return t("page.ftp.labels.notAvailable");
  }

  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatRelativeTime(value: string | null | undefined, t: TFunction) {
  if (!value) return t("page.ftp.labels.notAvailable");
  try {
    return dateFns.formatDistanceToNowStrict(dateFns.parseISO(value), {
      addSuffix: true,
    });
  } catch {
    return t("page.ftp.labels.notAvailable");
  }
}

function getElapsedTime(job: Job) {
  if (typeof job.duration_ms === "number") return job.duration_ms;
  if (job.completed_at) {
    const completed = dateFns.parseISO(job.completed_at).getTime();
    const started = dateFns.parseISO(job.started_at).getTime();
    return completed - started;
  }
  if (isActiveStatus(job.status)) {
    return Date.now() - dateFns.parseISO(job.started_at).getTime();
  }
  return null;
}

function hasNumericCount(value: number | null | undefined): value is number {
  return typeof value === "number" && !Number.isNaN(value);
}

function getProgress(job: Job) {
  if (!hasNumericCount(job.total_records) || job.total_records <= 0) {
    return null;
  }

  const processed = hasNumericCount(job.records_processed)
    ? job.records_processed
    : 0;
  const failed = hasNumericCount(job.records_failed) ? job.records_failed : 0;
  const ratio = Math.min(
    1,
    Math.max(0, (processed + failed) / job.total_records),
  );

  return {
    percentage: Math.round(ratio * 100),
    processed,
    failed,
    total: job.total_records,
  };
}

function getRecordSummary(job: Job, t: TFunction) {
  const total = hasNumericCount(job.total_records) ? job.total_records : null;
  const processed = hasNumericCount(job.records_processed)
    ? job.records_processed
    : null;
  const failed = hasNumericCount(job.records_failed)
    ? job.records_failed
    : null;

  if (total !== null) {
    if (total === 0) return t("page.ftp.labels.recordSummaryZero");

    if ((failed ?? 0) > 0) {
      return t("page.ftp.labels.recordSummaryWithFailures", {
        processed: processed ?? 0,
        total,
        failed: failed ?? 0,
      });
    }

    return t("page.ftp.labels.recordSummary", {
      processed: processed ?? 0,
      total,
    });
  }

  if (processed !== null && failed !== null) {
    if (failed > 0) {
      return t("page.ftp.labels.recordSummaryWithoutTotalWithFailures", {
        processed,
        failed,
      });
    }
    return t("page.ftp.labels.recordSummaryWithoutTotal", { processed });
  }

  if (processed !== null) {
    return t("page.ftp.labels.recordSummaryWithoutTotal", { processed });
  }

  if (failed !== null) {
    return t("page.ftp.labels.recordSummaryFailedOnly", { failed });
  }

  return t("page.ftp.labels.recordCountsNotTracked");
}

function statusLabel(status: JobStatus, t: TFunction) {
  return t(`page.ftp.statuses.${status}`);
}

function getJobInsight(job: Job, t: TFunction) {
  const errorMessage = job.error_message?.toLowerCase() ?? "";

  if (isActiveStatus(job.status)) {
    return {
      headline: t("page.ftp.insights.active.headline"),
      cause: t("page.ftp.insights.active.cause"),
      nextStep: t("page.ftp.insights.active.nextStep"),
    };
  }

  if (job.status === "TIMEOUT" || errorMessage.includes("timeout")) {
    return {
      headline: t("page.ftp.insights.timeout.headline"),
      cause: t("page.ftp.insights.timeout.cause"),
      nextStep: t("page.ftp.insights.timeout.nextStep"),
    };
  }

  if (job.status === "PARTIAL") {
    return {
      headline: t("page.ftp.insights.partial.headline"),
      cause: t("page.ftp.insights.partial.cause"),
      nextStep: t("page.ftp.insights.partial.nextStep"),
    };
  }

  if (
    errorMessage.includes("already in progress") ||
    errorMessage.includes("duplicate invocation")
  ) {
    return {
      headline: t("page.ftp.insights.duplicate.headline"),
      cause: t("page.ftp.insights.duplicate.cause"),
      nextStep: t("page.ftp.insights.duplicate.nextStep"),
    };
  }

  if (
    errorMessage.includes("configuration") ||
    errorMessage.includes("matches filename") ||
    errorMessage.includes("file type")
  ) {
    return {
      headline: t("page.ftp.insights.configuration.headline"),
      cause: t("page.ftp.insights.configuration.cause"),
      nextStep: t("page.ftp.insights.configuration.nextStep"),
    };
  }

  if (
    errorMessage.includes("validation") ||
    errorMessage.includes("bad request") ||
    errorMessage.includes("cannot mix")
  ) {
    return {
      headline: t("page.ftp.insights.validation.headline"),
      cause: t("page.ftp.insights.validation.cause"),
      nextStep: t("page.ftp.insights.validation.nextStep"),
    };
  }

  if (
    errorMessage.includes("connection refused") ||
    errorMessage.includes("api request error") ||
    errorMessage.includes("upstream") ||
    errorMessage.includes("econnrefused")
  ) {
    return {
      headline: t("page.ftp.insights.downstream.headline"),
      cause: t("page.ftp.insights.downstream.cause"),
      nextStep: t("page.ftp.insights.downstream.nextStep"),
    };
  }

  if (job.status === "FAILED") {
    return {
      headline: t("page.ftp.insights.failed.headline"),
      cause: t("page.ftp.insights.failed.cause"),
      nextStep: t("page.ftp.insights.failed.nextStep"),
    };
  }

  return {
    headline: t("page.ftp.insights.completed.headline"),
    cause: t("page.ftp.insights.completed.cause"),
    nextStep: t("page.ftp.insights.completed.nextStep"),
  };
}

function getInsightTone(job: Job): "danger" | "info" | "success" {
  if (isAttentionStatus(job.status)) return "danger";
  if (job.status === "COMPLETED") return "success";
  return "info";
}

function getProgressTone(job: Job): "danger" | "warning" | undefined {
  if (job.status === "FAILED" || job.status === "TIMEOUT") return "danger";
  if (job.status === "PARTIAL") return "warning";
  return undefined;
}

function JobStatusTag({ status, t }: { status: JobStatus; t: TFunction }) {
  if (isAttentionStatus(status)) {
    return (
      <Tag
        label={statusLabel(status, t)}
        display={{ tone: STATUS_TONES[status] }}
      />
    );
  }
  return (
    <span className={styles.statusPill} data-status={status}>
      <span className={styles.statusDot} aria-hidden={true} />
      {statusLabel(status, t)}
    </span>
  );
}

function ProgressBar({
  job,
  t,
  showLabel = true,
}: {
  job: Job;
  t: TFunction;
  showLabel?: boolean;
}) {
  const progress = getProgress(job);
  const tone = getProgressTone(job);

  if (!progress) {
    return showLabel ? (
      <span className={styles.mutedText}>{getRecordSummary(job, t)}</span>
    ) : null;
  }

  return (
    <div className={styles.progressGroup}>
      <div className={styles.progressBar} aria-hidden={true}>
        <div
          className={styles.progressValue}
          data-tone={tone}
          // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
          style={{ width: `${progress.percentage}%` }}
        />
      </div>
      {showLabel ? (
        <span className={styles.progressLabel}>{getRecordSummary(job, t)}</span>
      ) : null}
    </div>
  );
}

function TimestampStack({
  value,
  t,
  fallback,
}: {
  value: string | null | undefined;
  t: TFunction;
  fallback?: string;
}) {
  if (!value) {
    return (
      <span className={styles.mutedText}>
        {fallback ?? t("page.ftp.labels.notAvailable")}
      </span>
    );
  }

  return (
    <div className={styles.timestampStack}>
      <DateValue dateValue={value} />
      <span className={styles.mutedText}>{formatRelativeTime(value, t)}</span>
    </div>
  );
}

/* ---------- Now-processing rail ---------- */

function NowProcessingRail({
  jobs,
  onSelect,
  onViewAll,
  t,
}: {
  jobs: Job[];
  onSelect: (jobId: string) => void;
  onViewAll: () => void;
  t: TFunction;
}) {
  useTickingNow(15000);
  if (jobs.length === 0) return null;

  return (
    <Card
      layout="body-only"
      body={
        <div className={styles.nowProcessingCard}>
          <div className={styles.nowProcessingHeader}>
            <h2>
              <span
                className={styles.liveDot}
                data-live="true"
                aria-hidden={true}
              />
              {t("page.ftp.nowProcessing.headingWithCount", {
                count: jobs.length,
              })}
            </h2>
            <button
              type="button"
              className={styles.clearFiltersInline}
              onClick={onViewAll}
            >
              {t("page.ftp.nowProcessing.viewAll")}
            </button>
          </div>
          <div className={styles.nowProcessingRail}>
            {jobs.map((job) => {
              const elapsed = getElapsedTime(job);
              const stuck = (elapsed ?? 0) > STUCK_THRESHOLD_MS;
              return (
                <button
                  key={job.id}
                  type="button"
                  className={styles.runningCard}
                  data-stuck={stuck}
                  onClick={() => onSelect(job.id)}
                >
                  <div className={styles.runningCardHeader}>
                    <JobStatusTag status={job.status} t={t} />
                    {stuck ? (
                      <span
                        className={styles.stuckLabel}
                        title={t("page.ftp.labels.stuckTooltip")}
                      >
                        {t("page.ftp.labels.possiblyStuck")}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.runningCardFilename}>
                    {job.filename}
                  </div>
                  <ProgressBar job={job} t={t} />
                  <div className={styles.runningCardMeta}>
                    <span>{job.file_type}</span>
                    <span>•</span>
                    <span>
                      {t("page.ftp.nowProcessing.elapsed", {
                        value: formatDuration(elapsed, t),
                      })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      }
    />
  );
}

/* ---------- Charts ---------- */

const STATUS_MIX_COLORS = {
  COMPLETED: "var(--pallet-color-turquoise-500)",
  PARTIAL: "var(--pallet-color-orange-500)",
  FAILED: "var(--pallet-color-red-500)",
  TIMEOUT: "var(--pallet-color-red-100)",
} as const;

function describeDonutSlice(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const startOuter = {
    x: cx + outerR * Math.cos(startAngle),
    y: cy + outerR * Math.sin(startAngle),
  };
  const endOuter = {
    x: cx + outerR * Math.cos(endAngle),
    y: cy + outerR * Math.sin(endAngle),
  };
  const startInner = {
    x: cx + innerR * Math.cos(endAngle),
    y: cy + innerR * Math.sin(endAngle),
  };
  const endInner = {
    x: cx + innerR * Math.cos(startAngle),
    y: cy + innerR * Math.sin(startAngle),
  };
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}`,
    "Z",
  ].join(" ");
}

function StatusMixDonut({
  window: statsWindow,
  rangeLabel,
  t,
}: {
  window: JobStatsWindow | undefined;
  rangeLabel: string;
  t: TFunction;
}) {
  const segments = [
    {
      key: "COMPLETED" as const,
      value: statsWindow?.completed_jobs ?? 0,
    },
    {
      key: "PARTIAL" as const,
      value: statsWindow?.partial_jobs ?? 0,
    },
    {
      key: "FAILED" as const,
      value: statsWindow?.failed_jobs ?? 0,
    },
    {
      key: "TIMEOUT" as const,
      value: statsWindow?.timeout_jobs ?? 0,
    },
  ];
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  const cx = 50;
  const cy = 50;
  const outerR = 42;
  const innerR = 28;
  const drawnSegments = segments.filter((seg) => seg.value > 0);
  const onlySegment = drawnSegments.length === 1 ? drawnSegments[0] : undefined;

  let cursorAngle = -Math.PI / 2;
  const completedValue =
    segments.find((seg) => seg.key === "COMPLETED")?.value ?? 0;
  const successRate =
    total > 0 ? Math.round((completedValue / total) * 100) : 0;

  return (
    <Card
      layout="body-only"
      body={
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>{t("page.ftp.charts.statusMix.title")}</h3>
            <span className={styles.chartHeaderRange}>{rangeLabel}</span>
          </div>
          {total === 0 ? (
            <div className={styles.chartEmpty}>
              {t("page.ftp.charts.statusMix.empty")}
            </div>
          ) : (
            <div className={styles.donutLayout}>
              <svg
                viewBox="0 0 100 100"
                className={styles.donutSvg}
                role="img"
                aria-label={t("page.ftp.charts.statusMix.ariaLabel")}
              >
                {onlySegment ? (
                  <>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={outerR}
                      fill={STATUS_MIX_COLORS[onlySegment.key]}
                    />
                    <circle cx={cx} cy={cy} r={innerR} fill="white" />
                  </>
                ) : (
                  drawnSegments.map((seg) => {
                    const fraction = seg.value / total;
                    const angleSpan = fraction * 2 * Math.PI;
                    const startAngle = cursorAngle;
                    const endAngle = cursorAngle + angleSpan;
                    cursorAngle = endAngle;
                    return (
                      <path
                        key={seg.key}
                        d={describeDonutSlice(
                          cx,
                          cy,
                          outerR,
                          innerR,
                          startAngle,
                          endAngle,
                        )}
                        fill={STATUS_MIX_COLORS[seg.key]}
                      />
                    );
                  })
                )}
                <text
                  x={cx}
                  y={cy - 1}
                  textAnchor="middle"
                  className={styles.donutCenterValue}
                >
                  {total}
                </text>
                <text
                  x={cx}
                  y={cy + 9}
                  textAnchor="middle"
                  className={styles.donutCenterLabel}
                >
                  {t("page.ftp.charts.statusMix.totalLabel")}
                </text>
              </svg>
              <ul className={styles.donutLegend}>
                {segments.map((seg) => (
                  <li key={seg.key}>
                    <span
                      className={styles.legendSwatch}
                      // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
                      style={{ background: STATUS_MIX_COLORS[seg.key] }}
                      aria-hidden={true}
                    />
                    <span className={styles.legendLabel}>
                      {t(`page.ftp.statuses.${seg.key}`)}
                    </span>
                    <span className={styles.legendValue}>{seg.value}</span>
                    <span className={styles.legendPercent}>
                      {Math.round((seg.value / total) * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {total > 0 ? (
            <div className={styles.chartFooter}>
              <span className={styles.chartFooterLabel}>
                {t("page.ftp.successRate")}
              </span>
              <span className={styles.chartFooterValue}>{successRate}%</span>
            </div>
          ) : null}
        </div>
      }
    />
  );
}

function TopFileTypesBar({
  items,
  rangeLabel,
  t,
}: {
  items: ReadonlyArray<JobsByFileType>;
  rangeLabel: string;
  t: TFunction;
}) {
  const top = useMemo(
    () => [...items].sort((a, b) => b.count - a.count).slice(0, 8),
    [items],
  );
  const max = top[0]?.count ?? 0;

  return (
    <Card
      layout="body-only"
      body={
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>{t("page.ftp.charts.topFileTypes.title")}</h3>
            <span className={styles.chartHeaderRange}>{rangeLabel}</span>
          </div>
          {top.length === 0 ? (
            <div className={styles.chartEmpty}>
              {t("page.ftp.charts.topFileTypes.empty")}
            </div>
          ) : (
            <ul
              className={styles.fileTypeBars}
              aria-label={t("page.ftp.charts.topFileTypes.ariaLabel")}
            >
              {top.map((item) => (
                <li key={item.file_type}>
                  <span className={styles.fileTypeName} title={item.file_type}>
                    {item.file_type}
                  </span>
                  <div
                    className={styles.fileTypeBarTrack}
                    aria-hidden={true}
                    role="presentation"
                  >
                    <div
                      className={styles.fileTypeBarFill}
                      // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
                      style={{
                        width: max > 0 ? `${(item.count / max) * 100}%` : "0%",
                      }}
                    />
                  </div>
                  <span className={styles.fileTypeCount}>{item.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      }
    />
  );
}

/* ---------- File-type popover ---------- */

type FileTypeItem = { fileType: string; count: number };

function FileTypePopover({
  trigger,
  items,
  included,
  onChange,
  onClose,
  t,
}: {
  trigger: HTMLElement;
  items: FileTypeItem[];
  included: string[];
  onChange: (next: string[]) => void;
  onClose: () => void;
  t: TFunction;
}) {
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState(() => {
    const rect = trigger.getBoundingClientRect();
    return { top: rect.bottom + 6, left: rect.left };
  });

  useEffect(() => {
    function handleResize() {
      const rect = trigger.getBoundingClientRect();
      setPosition({ top: rect.bottom + 6, left: rect.left });
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKey);
    };
  }, [trigger, onClose]);

  const filtered = items.filter((item) =>
    item.fileType.toLowerCase().includes(search.trim().toLowerCase()),
  );

  function toggleIncluded(fileType: string) {
    const isIncluded = included.includes(fileType);
    onChange(
      isIncluded
        ? included.filter((entry) => entry !== fileType)
        : [...included, fileType],
    );
  }

  function clearAll() {
    onChange([]);
  }

  const includedCount = included.length;

  return createPortal(
    <>
      <div
        className={styles.popoverBackdrop}
        onClick={onClose}
        aria-hidden={true}
      />
      <div
        className={styles.popoverPanel}
        // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
        style={{ top: position.top, left: position.left }}
        role="dialog"
        aria-label={t("page.ftp.filters.fileTypePopoverTitle")}
      >
        <div className={styles.popoverHeader}>
          <strong>{t("page.ftp.filters.fileTypePopoverTitle")}</strong>
          <p>{t("page.ftp.filters.fileTypePopoverDescription")}</p>
        </div>
        <div className={styles.popoverBody}>
          {/* No wrapper: `.popoverSearch` was exactly `width: 100%`, which
              pallet's `Search` now does natively (SR-301 phase 6, OQ-4). */}
          <Search
            value={search}
            aria-label={t("page.ftp.filters.fileTypeSearchPlaceholder")}
            placeholder={t("page.ftp.filters.fileTypeSearchPlaceholder")}
            onChange={(value) => setSearch(String(value))}
            onClear={() => setSearch("")}
          />

          <div className={styles.popoverSection}>
            <div className={styles.popoverList}>
              {filtered.length === 0 ? (
                <div className={styles.popoverEmpty}>{t("page.ftp.empty")}</div>
              ) : (
                filtered.map((item) => (
                  <label
                    key={`inc-${item.fileType}`}
                    className={styles.popoverRow}
                  >
                    <input
                      type="checkbox"
                      checked={included.includes(item.fileType)}
                      onChange={() => toggleIncluded(item.fileType)}
                    />
                    <span className={styles.popoverRowLabel}>
                      {item.fileType}
                    </span>
                    <span className={styles.popoverRowCount}>{item.count}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
        <div className={styles.popoverFooter}>
          <Button
            kind="text-only"
            text={t("page.ftp.filters.fileTypeClear")}
            variant="tertiary"
            onPress={clearAll}
            isDisabled={includedCount === 0}
          />
          <Button
            kind="text-only"
            variant="primary"
            text={t("page.ftp.filters.fileTypeApply")}
            onPress={onClose}
          />
        </div>
      </div>
    </>,
    document.body,
  );
}

/* ---------- Download anchor ---------- */

function DownloadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={true}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function DownloadAnchor({ href, label }: { href: string; label: string }) {
  return (
    <a
      className={styles.downloadAnchor}
      href={href}
      rel="noopener"
      target="_blank"
    >
      <DownloadIcon />
      <span>{label}</span>
    </a>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function getPrimaryDownloadUrl(job: Job) {
  if (typeof job.download_url === "string" && job.download_url.length > 0) {
    return job.download_url;
  }

  if (job.output_files?.length !== 1) {
    return undefined;
  }

  const singleOutput = job.output_files[0];
  return typeof singleOutput?.download_url === "string" &&
    singleOutput.download_url.length > 0
    ? singleOutput.download_url
    : undefined;
}

function hasNoProcessedRecords(job: Job) {
  return (
    job.status === "COMPLETED" &&
    job.records_processed === 0 &&
    job.records_failed === 0
  );
}

function fileTypeTriggerLabel(included: string[], t: TFunction) {
  if (included.length === 0) {
    return t("page.ftp.filters.fileTypeTriggerAll");
  }
  return t("page.ftp.filters.fileTypeTriggerIncluded", {
    count: included.length,
  });
}

/* ---------- Drawer ---------- */

function JobDrawer({
  job,
  log,
  isJobLoading,
  isLogLoading,
  onClose,
  returnFocusTo,
  t,
}: {
  job?: Job;
  log?: JobLog;
  isJobLoading: boolean;
  isLogLoading: boolean;
  onClose: () => void;
  returnFocusTo: HTMLElement | null;
  t: TFunction;
}) {
  const [logTab, setLogTab] = useState<"raw" | "parsed">("raw");
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useTickingNow(30000);

  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
      returnFocusTo?.focus();
    };
  }, [onClose, returnFocusTo]);

  const parsedLog = log?.parsed
    ? JSON.stringify(log.parsed, null, 2)
    : undefined;
  const rawLog = log?.raw;
  const hasParsedLog = typeof parsedLog === "string" && parsedLog.length > 0;
  const hasRawLog = typeof rawLog === "string" && rawLog.length > 0;
  const visibleLog = logTab === "parsed" && hasParsedLog ? parsedLog : rawLog;

  const insight = job ? getJobInsight(job, t) : undefined;
  const insightTone = job ? getInsightTone(job) : undefined;
  const primaryDownloadUrl = job ? getPrimaryDownloadUrl(job) : undefined;
  const hasNoProcessedOutput = job ? hasNoProcessedRecords(job) : false;

  return createPortal(
    <div
      className={styles.drawerBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label={t("page.ftp.drawer.title")}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <div className={styles.drawerHeaderTop}>
            <div>
              <span className={styles.drawerEyebrow}>
                {t("page.ftp.drawer.title")}
              </span>
              <h2 className={styles.drawerTitle}>
                {job ? job.filename : t("page.ftp.labels.notAvailable")}
              </h2>
            </div>
            <Button
              kind="icon-only"
              label={t("page.ftp.actions.close")}
              variant="tertiary"
              icon="X"
              onPress={onClose}
            />
          </div>
          {job ? (
            <div className={styles.drawerHeaderMeta}>
              <JobStatusTag status={job.status} t={t} />
              <span>{job.file_type}</span>
              <span>•</span>
              <span>
                {t("page.ftp.detail.duration")}:{" "}
                {formatDuration(getElapsedTime(job), t)}
              </span>
            </div>
          ) : null}
        </div>

        <div className={styles.drawerBody}>
          {!job && isJobLoading ? (
            <div className={styles.tableState}>
              <Loader type="page" />
            </div>
          ) : !job ? (
            <EmptyState heading={t("page.ftp.empty")} />
          ) : (
            <>
              <div className={styles.drawerActions}>
                {(job.output_files?.length ?? 0) <= 1 && primaryDownloadUrl ? (
                  <DownloadAnchor
                    href={primaryDownloadUrl}
                    label={t("page.ftp.actions.download")}
                  />
                ) : null}
                {log?.log_url ? (
                  <DownloadAnchor
                    href={log.log_url}
                    label={t("page.ftp.actions.downloadLog")}
                  />
                ) : null}
                <Button
                  kind="text-only"
                  text={t("page.ftp.actions.copyJobId")}
                  variant="secondary"
                  onPress={() =>
                    copyText(job.id, t("page.ftp.messages.jobIdCopied"), t)
                  }
                />
                {job.error_message ? (
                  <Button
                    kind="text-only"
                    text={t("page.ftp.actions.copyError")}
                    variant="secondary"
                    onPress={() =>
                      copyText(
                        job.error_message ?? "",
                        t("page.ftp.messages.errorCopied"),
                        t,
                      )
                    }
                  />
                ) : null}
                {hasRawLog ? (
                  <Button
                    kind="text-only"
                    text={t("page.ftp.actions.copyLog")}
                    variant="secondary"
                    onPress={() =>
                      copyText(
                        rawLog ?? "",
                        t("page.ftp.messages.logCopied"),
                        t,
                      )
                    }
                  />
                ) : null}
              </div>

              {insight && insightTone ? (
                <div className={styles.insightCard} data-tone={insightTone}>
                  <span className={styles.insightHeadline}>
                    {insight.headline}
                  </span>
                  <div className={styles.insightDetail}>
                    <span className={styles.insightLabel}>
                      {t("page.ftp.detail.likelyCause")}
                    </span>
                    <span>{insight.cause}</span>
                  </div>
                  <div className={styles.insightDetail}>
                    <span className={styles.insightLabel}>
                      {t("page.ftp.detail.nextStep")}
                    </span>
                    <span>{insight.nextStep}</span>
                  </div>
                </div>
              ) : null}

              {job.error_message ? (
                <div className={styles.errorBox}>
                  <span className={styles.insightLabel}>
                    {t("page.ftp.detail.error")}
                  </span>
                  <code>{job.error_message}</code>
                </div>
              ) : null}

              {job.output_files && job.output_files.length > 1 ? (
                <div className={styles.drawerSection}>
                  <h3>{t("page.ftp.detail.outputFiles")}</h3>
                  <ul className={styles.outputFileList}>
                    {job.output_files.map((output) => (
                      <li
                        key={`${output.output_name}:${output.filename}`}
                        className={styles.outputFileItem}
                      >
                        {typeof output.download_url === "string" ? (
                          <DownloadAnchor
                            href={output.download_url}
                            label={output.filename}
                          />
                        ) : (
                          <span>{output.filename}</span>
                        )}
                        <span className={styles.outputFileMeta}>
                          {formatFileSize(output.file_size_bytes)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {hasNoProcessedOutput ? (
                <div className={styles.insightCard} data-tone="success">
                  <span className={styles.insightHeadline}>
                    {t("page.ftp.detail.noOutputHeadline")}
                  </span>
                  <span>{t("page.ftp.detail.noOutputDetail")}</span>
                </div>
              ) : null}

              <div className={styles.drawerSection}>
                <dl className={styles.detailGrid}>
                  <DetailItem label={t("page.ftp.detail.jobId")}>
                    <code>{job.id}</code>
                  </DetailItem>
                  <DetailItem label={t("page.ftp.detail.fileType")}>
                    {job.file_type}
                  </DetailItem>
                  <DetailItem label={t("page.ftp.detail.started")}>
                    <TimestampStack value={job.started_at} t={t} />
                  </DetailItem>
                  <DetailItem label={t("page.ftp.detail.completed")}>
                    <TimestampStack
                      value={job.completed_at}
                      t={t}
                      fallback={t("page.ftp.labels.inProgress")}
                    />
                  </DetailItem>
                  <DetailItem label={t("page.ftp.detail.records")}>
                    <ProgressBar job={job} t={t} />
                  </DetailItem>
                </dl>
              </div>

              <div className={styles.drawerSection}>
                <h3>{t("page.ftp.detail.log")}</h3>
                {isLogLoading ? (
                  <div className={styles.tableState}>
                    <Loader type="page" />
                  </div>
                ) : !hasRawLog && !hasParsedLog ? (
                  <p className={styles.logUnavailable}>
                    {t("page.ftp.detail.logUnavailable")}
                  </p>
                ) : (
                  <>
                    {hasParsedLog ? (
                      <div className={styles.logTabs} role="tablist">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={logTab === "raw"}
                          className={styles.logTab}
                          data-selected={logTab === "raw"}
                          onClick={() => setLogTab("raw")}
                        >
                          {t("page.ftp.detail.logRawTab")}
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={logTab === "parsed"}
                          className={styles.logTab}
                          data-selected={logTab === "parsed"}
                          onClick={() => setLogTab("parsed")}
                        >
                          {t("page.ftp.detail.logParsedTab")}
                        </button>
                      </div>
                    ) : null}
                    <pre className={styles.logViewer}>{visibleLog}</pre>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DetailItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.detailItem}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

async function copyText(value: string, message: string, t: TFunction) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  } catch {
    toast.error(t("page.ftp.messages.copyFailed"));
  }
}

/* ---------- Main route component ---------- */

export function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [manualRangeNow, setManualRangeNow] = useState(() => Date.now());
  const [fileTypePopoverAnchor, setFileTypePopoverAnchor] =
    useState<HTMLElement | null>(null);
  const fileTypeTriggerRef = useRef<HTMLButtonElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  const returnFocusIdRef = useRef<string | null>(null);
  const client = useFtpApi();
  const token = LocalStorage.getGlobalJwt() ?? "";

  const tickingNow = useTickingNow(30000);
  const rangeNow = autoRefresh ? tickingNow : manualRangeNow;

  const selectedFileTypes = useMemo(
    () => parseCsvValue(search.fileTypes),
    [search.fileTypes],
  );
  const fileTypesParam =
    selectedFileTypes.length > 0 ? selectedFileTypes.join(",") : undefined;
  const rangeFromParam = useMemo(
    () => getRangeFrom(search.range, rangeNow),
    [search.range, rangeNow],
  );

  const statsRefreshInterval = autoRefresh ? 30000 : false;

  const liveStatsQuery = client.useQuery(
    "get",
    "/stats",
    {
      params: {
        query: { token },
      },
    },
    {
      refetchInterval: statsRefreshInterval,
      placeholderData: (previousData) => previousData,
    },
  );

  const statsQuery = client.useQuery(
    "get",
    "/stats",
    {
      params: {
        query: {
          token,
          file_types: fileTypesParam,
          from: rangeFromParam,
        },
      },
    },
    {
      refetchInterval: statsRefreshInterval,
      placeholderData: (previousData) => previousData,
    },
  );

  const hasKnownActiveJobs =
    (liveStatsQuery.data?.data.overall.active_jobs ?? 0) > 0;
  const liveRefreshInterval = autoRefresh
    ? hasKnownActiveJobs
      ? 15000
      : 60000
    : false;

  const activeQuery = client.useQuery(
    "get",
    "/jobs",
    {
      params: {
        query: {
          token,
          limit: 6,
          statuses: ACTIVE_STATUS_PARAM,
        },
      },
    },
    {
      refetchInterval: liveRefreshInterval,
      placeholderData: (previousData) => previousData,
      enabled: hasKnownActiveJobs || !liveStatsQuery.data,
    },
  );

  const historyQuery = client.useQuery(
    "get",
    "/jobs",
    {
      params: {
        query: {
          token,
          page: search.page + 1,
          limit: HISTORY_PAGE_SIZE,
          statuses: getStatusesParam(search.view),
          q: search.q || undefined,
          file_types: fileTypesParam,
          from: rangeFromParam,
        },
      },
    },
    {
      refetchInterval: liveRefreshInterval,
      placeholderData: (previousData) => previousData,
    },
  );

  const selectedJobQuery = client.useQuery(
    "get",
    "/jobs/{jobId}",
    {
      params: {
        path: { jobId: search.selected ?? "" },
        query: { token },
      },
    },
    {
      enabled: typeof search.selected === "string",
      refetchInterval: liveRefreshInterval,
      placeholderData: (previousData) => previousData,
    },
  );

  const selectedLogQuery = client.useQuery(
    "get",
    "/jobs/{jobId}/logs",
    {
      params: {
        path: { jobId: search.selected ?? "" },
        query: { token },
      },
    },
    {
      enabled: typeof search.selected === "string",
      refetchInterval: liveRefreshInterval,
      retry: false,
      placeholderData: (previousData) => previousData,
    },
  );

  const stats = statsQuery.data?.data;
  const activeJobs =
    hasKnownActiveJobs || !liveStatsQuery.data
      ? (activeQuery.data?.data ?? [])
      : [];
  const historyJobs = historyQuery.data?.data ?? [];
  const pagination = historyQuery.data?.meta.pagination;
  const historyError = historyQuery.isError && !historyQuery.data;
  const historyRefiltering = historyQuery.isPlaceholderData;

  const fileTypeItems = useMemo<FileTypeItem[]>(() => {
    const items = new Map<string, FileTypeItem>();
    for (const entry of liveStatsQuery.data?.data.file_types ?? []) {
      items.set(entry.file_type, {
        fileType: entry.file_type,
        count: entry.count,
      });
    }
    for (const fileType of selectedFileTypes) {
      if (!items.has(fileType)) {
        items.set(fileType, { fileType, count: 0 });
      }
    }
    return Array.from(items.values()).sort((left, right) =>
      left.fileType.localeCompare(right.fileType),
    );
  }, [liveStatsQuery.data?.data.file_types, selectedFileTypes]);

  const knownJobs = [...activeJobs, ...historyJobs];
  const selectedJob =
    selectedJobQuery.data?.data ??
    knownJobs.find((job) => job.id === search.selected);
  const selectedLog = selectedLogQuery.data?.data;

  const latestUpdatedAt = [
    liveStatsQuery.dataUpdatedAt,
    statsQuery.dataUpdatedAt,
    activeQuery.dataUpdatedAt,
    historyQuery.dataUpdatedAt,
    selectedJobQuery.dataUpdatedAt,
    selectedLogQuery.dataUpdatedAt,
  ].reduce((maxValue, currentValue) => Math.max(maxValue, currentValue), 0);

  logger.trace("Rendering FTP dashboard", {
    selected_job: search.selected,
    status_view: search.view,
    range: search.range,
    page: search.page,
    file_types: search.fileTypes,
  });

  const goTo = (next: Partial<FtpSearch>) => {
    navigate({
      to: "/$lang/orgs/$org/internal/whs/$location/ftp",
      params,
      search: {
        page: next.page ?? search.page,
        q: next.q ?? search.q,
        view: next.view ?? search.view,
        range: next.range ?? search.range,
        fileTypes: next.fileTypes ?? search.fileTypes,
        selected: Object.prototype.hasOwnProperty.call(next, "selected")
          ? next.selected
          : search.selected,
      },
      resetScroll: false,
    });
  };

  const refreshAll = () => {
    setManualRangeNow(Date.now());
    void Promise.all([
      liveStatsQuery.refetch(),
      statsQuery.refetch(),
      activeQuery.refetch(),
      historyQuery.refetch(),
      ...(search.selected
        ? [selectedJobQuery.refetch(), selectedLogQuery.refetch()]
        : []),
    ]);
  };

  const handleStatusChange = (view: StatusView) =>
    goTo({ view, page: 0, selected: search.selected });

  const handleAutoRefreshChange = (isSelected: boolean) => {
    setManualRangeNow(Date.now());
    setAutoRefresh(isSelected);
  };

  const handleRangeChange = (range: RangeView) =>
    goTo({ range, page: 0, selected: search.selected });

  const handleFileTypesChange = (next: string[]) =>
    goTo({
      page: 0,
      fileTypes: next.join(","),
      selected: search.selected,
    });

  const filtersDirty =
    search.q !== "" ||
    search.view !== "all" ||
    search.range !== "24h" ||
    selectedFileTypes.length > 0;

  const resetFilters = () =>
    goTo({
      page: 0,
      q: "",
      view: "all",
      range: "24h",
      fileTypes: "",
      selected: search.selected,
    });

  const activeChips: Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }> = [];
  if (search.q) {
    activeChips.push({
      key: "q",
      label: `"${search.q}"`,
      onRemove: () => goTo({ q: "", page: 0, selected: search.selected }),
    });
  }
  for (const fileType of selectedFileTypes) {
    activeChips.push({
      key: `include-${fileType}`,
      label: fileType,
      onRemove: () =>
        handleFileTypesChange(
          selectedFileTypes.filter((value) => value !== fileType),
        ),
    });
  }

  const initialLoading =
    !statsQuery.data &&
    !historyQuery.data &&
    (statsQuery.isLoading || historyQuery.isLoading);

  if (initialLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader type="page" />
      </div>
    );
  }

  const filteredOverall = stats?.overall;
  const liveStats = liveStatsQuery.data?.data;
  const chartRangeLabel =
    search.range === "all"
      ? t("page.ftp.filters.rangeAll")
      : search.range === "24h"
        ? t("page.ftp.filters.range24h")
        : search.range === "7d"
          ? t("page.ftp.filters.range7d")
          : t("page.ftp.filters.range30d");
  const activeCount = filteredOverall?.active_jobs ?? 0;
  const attentionCount = filteredOverall?.attention_jobs ?? 0;

  return (
    <div className="bg-surface h-full w-full">
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <div>
            <h1>{t("page.ftp.title")}</h1>
            <p>{t("page.ftp.subtitle")}</p>
          </div>
          <div className={styles.pageActionsColumn}>
            <div className={styles.pageActions}>
              <Switch
                isSelected={autoRefresh}
                value="auto-refresh"
                name="auto-refresh"
                label={t("page.ftp.controls.autoRefresh")}
                display={{ orientation: "row-reverse" }}
                onChange={handleAutoRefreshChange}
              />
              <Button
                kind="text-only"
                variant="primary"
                text={t("page.ftp.actions.refresh")}
                onPress={refreshAll}
              />
            </div>
            <div className={styles.systemStatus}>
              <span className={styles.lastUpdated}>
                <span
                  className={styles.liveDot}
                  data-live={autoRefresh}
                  aria-hidden={true}
                />
                {latestUpdatedAt > 0
                  ? t("page.ftp.controls.lastUpdated", {
                      value: dateFns.formatDistanceToNowStrict(latestUpdatedAt),
                    })
                  : t("page.ftp.controls.lastUpdatedNever")}
              </span>
              <span className={styles.systemStatusItem}>
                {t("page.ftp.stats.lastSuccess")}:{" "}
                {liveStats?.last_success_at
                  ? formatRelativeTime(liveStats.last_success_at, t)
                  : t("page.ftp.stats.lastSuccessNone")}
              </span>
            </div>
          </div>
        </header>

        <NowProcessingRail
          jobs={activeJobs}
          onSelect={(jobId) => goTo({ selected: jobId })}
          onViewAll={() => handleStatusChange("active")}
          t={t}
        />

        <Card
          layout="body-only"
          body={
            <>
              <div
                className={styles.filterBar}
                role="group"
                aria-label={t("page.ftp.filters.barLabel")}
              >
                <div className={styles.filterBarRow}>
                  <div className={styles.filterGroup} role="tablist">
                    {(
                      [
                        { id: "all", label: t("page.ftp.filters.statusAll") },
                        {
                          id: "active",
                          label: t("page.ftp.filters.statusActive"),
                          badge: activeCount,
                        },
                        {
                          id: "attention",
                          label: t("page.ftp.filters.statusAttention"),
                          badge: attentionCount,
                          emphasis: "danger" as const,
                        },
                        {
                          id: "completed",
                          label: t("page.ftp.filters.statusCompleted"),
                        },
                      ] as Array<{
                        id: StatusView;
                        label: string;
                        badge?: number;
                        emphasis?: "danger";
                      }>
                    ).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        role="tab"
                        aria-selected={search.view === option.id}
                        className={styles.filterGroupItem}
                        data-selected={search.view === option.id}
                        data-emphasis={option.emphasis}
                        onClick={() => handleStatusChange(option.id)}
                      >
                        {option.label}
                        {typeof option.badge === "number" &&
                        option.badge > 0 ? (
                          <span className={styles.filterGroupItemBadge}>
                            {option.badge}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>

                  {/* `Search` owns no width (SR-301 phase 6, OQ-4); the flex
                  sizing that used to sit on it now sits on this wrapper. */}
                  <div className={styles.searchControl}>
                    <Search
                      value={search.q}
                      aria-label={t("page.ftp.search.ariaLabel")}
                      placeholder={t("page.ftp.search.placeholder")}
                      onChange={(value) =>
                        goTo({
                          q: String(value),
                          page: 0,
                          selected: search.selected,
                        })
                      }
                      onClear={() =>
                        goTo({ q: "", page: 0, selected: search.selected })
                      }
                    />
                  </div>
                </div>

                <div className={styles.filterBarRow}>
                  <button
                    ref={fileTypeTriggerRef}
                    type="button"
                    className={styles.filterTrigger}
                    data-active={selectedFileTypes.length > 0}
                    onClick={() =>
                      setFileTypePopoverAnchor((current) =>
                        current ? null : fileTypeTriggerRef.current,
                      )
                    }
                  >
                    <span>{fileTypeTriggerLabel(selectedFileTypes, t)}</span>
                    <span
                      className={styles.filterTriggerCaret}
                      aria-hidden={true}
                    >
                      ▾
                    </span>
                  </button>

                  <div className={styles.filterGroup}>
                    {(
                      [
                        { id: "all", label: t("page.ftp.filters.rangeAll") },
                        { id: "24h", label: t("page.ftp.filters.range24h") },
                        { id: "7d", label: t("page.ftp.filters.range7d") },
                        { id: "30d", label: t("page.ftp.filters.range30d") },
                      ] as Array<{ id: RangeView; label: string }>
                    ).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={styles.filterGroupItem}
                        data-selected={search.range === option.id}
                        onClick={() => handleRangeChange(option.id)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  <div className={styles.filterSpacer} />

                  <button
                    type="button"
                    className={styles.clearFiltersInline}
                    onClick={resetFilters}
                    data-hidden={!filtersDirty}
                    tabIndex={filtersDirty ? 0 : -1}
                    aria-hidden={!filtersDirty}
                  >
                    {t("page.ftp.actions.clearFilters")}
                  </button>
                </div>
              </div>

              {activeChips.length > 0 ? (
                <div className={styles.activeFilterChips}>
                  {activeChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      className={styles.activeFilterChip}
                      onClick={chip.onRemove}
                    >
                      <span>{chip.label}</span>
                      <span aria-hidden={true}>✕</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          }
        />

        <section className={styles.chartsStrip}>
          <StatusMixDonut
            window={filteredOverall}
            rangeLabel={chartRangeLabel}
            t={t}
          />
          <TopFileTypesBar
            items={stats?.file_types ?? []}
            rangeLabel={chartRangeLabel}
            t={t}
          />
        </section>

        <Card
          layout="body-only"
          body={
            <>
              <div
                className={styles.tableViewport}
                aria-busy={historyRefiltering}
              >
                {historyRefiltering ? (
                  <div className={styles.tableBusyOverlay} aria-hidden={true}>
                    <Loader type="page" />
                  </div>
                ) : null}
                <div
                  className={styles.tableScroller}
                  data-busy={historyRefiltering}
                >
                  {historyQuery.isLoading && !historyQuery.data ? (
                    <div className={styles.tableState}>
                      <Loader type="page" />
                    </div>
                  ) : historyError ? (
                    <div className={styles.tableState}>
                      <AppError
                        description={t("page.ftp.failed")}
                        resolution={t("page.ftp.failureSupport")}
                      />
                    </div>
                  ) : historyJobs.length === 0 ? (
                    <div className={styles.tableState}>
                      <EmptyState heading={t("page.ftp.empty")}>
                        {filtersDirty ? (
                          <Button
                            kind="text-only"
                            variant="secondary"
                            text={t("page.ftp.actions.clearFilters")}
                            onPress={resetFilters}
                          />
                        ) : null}
                      </EmptyState>
                    </div>
                  ) : (
                    <Table
                      aria-label={t("page.ftp.table.ariaLabel")}
                      isResizable={false}
                    >
                      <TableHead>
                        <TableHeaderCell isRowHeader={true}>
                          {t("page.ftp.table.status")}
                        </TableHeaderCell>
                        <TableHeaderCell>
                          {t("page.ftp.table.file")}
                        </TableHeaderCell>
                        <TableHeaderCell>
                          {t("page.ftp.table.started")}
                        </TableHeaderCell>
                        <TableHeaderCell>
                          {t("page.ftp.table.duration")}
                        </TableHeaderCell>
                        <TableHeaderCell>
                          {t("page.ftp.table.records")}
                        </TableHeaderCell>
                        <TableHeaderCell>
                          {t("page.ftp.table.result")}
                        </TableHeaderCell>
                      </TableHead>
                      <TableBody>
                        {historyJobs.map((job) => {
                          const elapsed = getElapsedTime(job);
                          const stuck =
                            isActiveStatus(job.status) &&
                            (elapsed ?? 0) > STUCK_THRESHOLD_MS;
                          const resultText = job.error_message
                            ? job.error_message
                            : job.status === "COMPLETED"
                              ? t("page.ftp.labels.completedSuccessfully")
                              : isActiveStatus(job.status)
                                ? t("page.ftp.labels.inProgress")
                                : t("page.ftp.labels.noErrorReported");
                          const primaryDownloadUrl = getPrimaryDownloadUrl(job);
                          const hasNoProcessedOutput =
                            hasNoProcessedRecords(job);
                          return (
                            <TableRow
                              key={job.id}
                              ref={(node) => {
                                if (node) rowRefs.current.set(job.id, node);
                                else rowRefs.current.delete(job.id);
                              }}
                              onAction={() => {
                                returnFocusIdRef.current = job.id;
                                goTo({ selected: job.id });
                              }}
                              data-selected={search.selected === job.id}
                              data-test-role="ftp-job-row"
                            >
                              <TableCell>
                                <div className={styles.statusCell}>
                                  <JobStatusTag status={job.status} t={t} />
                                  {stuck ? (
                                    <span
                                      className={styles.stuckLabel}
                                      title={t("page.ftp.labels.stuckTooltip")}
                                    >
                                      {t("page.ftp.labels.possiblyStuck")}
                                    </span>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className={styles.fileCell}>
                                  <strong>{job.filename}</strong>
                                  <span className={styles.mutedText}>
                                    {job.file_type}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <TimestampStack value={job.started_at} t={t} />
                              </TableCell>
                              <TableCell>
                                {elapsed === null
                                  ? t("page.ftp.labels.notAvailable")
                                  : formatDuration(elapsed, t)}
                              </TableCell>
                              <TableCell>
                                <ProgressBar job={job} t={t} />
                              </TableCell>
                              <TableCell>
                                <div className={styles.resultCell}>
                                  <span className={styles.resultText}>
                                    {resultText}
                                  </span>
                                  {primaryDownloadUrl ? (
                                    <a
                                      rel="noopener"
                                      target="_blank"
                                      href={primaryDownloadUrl}
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                      className={styles.clearFiltersInline}
                                    >
                                      {(job.output_files?.length ?? 0) > 1
                                        ? t("page.ftp.actions.downloadCount", {
                                            count:
                                              job.output_files?.length ?? 0,
                                          })
                                        : t("page.ftp.actions.download")}
                                    </a>
                                  ) : hasNoProcessedOutput ? (
                                    <span className={styles.noOutputLabel}>
                                      {t("page.ftp.labels.noOutput")}
                                    </span>
                                  ) : null}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {pagination && pagination.total_pages > 1 ? (
                <div className={styles.tableFooter}>
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    nextButton={{
                      isDisabled: !pagination.has_next_page,
                      onPress: () => goTo({ page: search.page + 1 }),
                    }}
                    previousButton={{
                      isDisabled: !pagination.has_previous_page,
                      onPress: () =>
                        goTo({ page: Math.max(0, search.page - 1) }),
                    }}
                    jumpTo={(page) => goTo({ page: Math.max(page, 0) })}
                  />
                </div>
              ) : null}
            </>
          }
        />
      </div>

      {fileTypePopoverAnchor ? (
        <FileTypePopover
          trigger={fileTypePopoverAnchor}
          items={fileTypeItems}
          included={selectedFileTypes}
          onChange={handleFileTypesChange}
          onClose={() => setFileTypePopoverAnchor(null)}
          t={t}
        />
      ) : null}

      {search.selected ? (
        <JobDrawer
          job={selectedJob}
          log={selectedLog}
          isJobLoading={selectedJobQuery.isLoading && !selectedJob}
          isLogLoading={selectedLogQuery.isLoading}
          onClose={() => goTo({ selected: undefined })}
          returnFocusTo={
            returnFocusIdRef.current
              ? (rowRefs.current.get(returnFocusIdRef.current) ?? null)
              : null
          }
          t={t}
        />
      ) : null}
    </div>
  );
}
